import { Space, Tag } from 'antd';
import { useCallback, useEffect } from 'react';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useTranslation } from 'react-i18next';
import {
  ElementTypeEnum,
  ElementTypeSelectOptions,
  MarkupTypeEnum,
  PartialMarkup,
} from 'domains/markupDomain';
import { RevxButton } from 'components/RevxButton';

const ElementOverrideTable = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  // table data processing
  const data = useAppSelector((state) => {
    if (state.markup.elementOverrideMarkups?.content) {
      return state.markup.elementOverrideMarkups.content.map((m) => {
        return {
          ...m,
          markupPct: m.markupRanges[0].markupPct,
        } as PartialMarkup;
      });
    } else {
      return [];
    }
  });
  const total = useAppSelector(
    (state) => state.markup.elementOverrideMarkups?.totalElements || 0
  );
  const currentPage = useAppSelector(
    (state) => state.markup.elementOverrideCurrentPage
  );
  const pageSize = useAppSelector(
    (state) => state.markup.elementOverridePageSize
  );
  const loading = useAppSelector(
    (state) => state.loading.effects.markup.getElementOverrideMarkups
  );

  useEffect(() => {
    dispatch.markup.getElementOverrideMarkups({
      page: { currentPage, pageSize },
      query: { type: MarkupTypeEnum.ELEMENT_OVERRIDE },
    });
  }, [currentPage, pageSize, dispatch.markup]);

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          const currentPage = pagination.current;
          const pageSize = pagination.pageSize;
          dispatch.markup.setState({
            elementOverrideCurrentPage: currentPage,
            elementOverridePageSize: pageSize,
          });
          break;
      }
    },
    [dispatch.markup]
  );
  const columns: TableColumnsType<PartialMarkup> = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      fixed: 'left',
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      width: 200,
    },
    {
      title: t('platform.markup.labels.jobElement'),
      dataIndex: 'elementType',
      render: (value: ElementTypeEnum) =>
        ElementTypeSelectOptions.find((option) => option.code === value)
          ?.name || '',
    },
    {
      title: t('platform.markup.columns.markupPct'),
      dataIndex: 'markupPct',
      valueType: 'percentage',
    },
    {
      title: t('platform.markup.columns.startDate'),
      dataIndex: 'startDt',
    },
    {
      title: t('platform.markup.columns.endDate'),
      dataIndex: 'endDt',
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'createAt',
      valueType: 'time',
    },
    {
      title: t('common.active'),
      fixed: 'right',
      dataIndex: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      render: (value, record) => (
        <Space size="small">
          <RevxButton
            type="link"
            onClick={() =>
              dispatch.markup.setState({
                curElementOverrideMarkup: {
                  ...record,
                },
              })
            }
          >
            {t('common.view')}
          </RevxButton>
        </Space>
      ),
    },
  ];

  return (
    <RevxTable<PartialMarkup>
      loading={loading}
      columns={columns}
      dataSource={data}
      pagination={{
        pageSize,
        total,
      }}
      onChange={onTableChange}
    />
  );
};

export default ElementOverrideTable;
